/**
 * `/__ui/notifications` — the visual-regression harness for M5 (the same trick B4's board
 * harness uses). It renders with **no session, no api and no database**, so a served build can
 * be hit-tested against `discovery/ui-reference/notifications/*.png`.
 *
 * Three surfaces on one page, selected by `?screen=`:
 *   `bell` (default) · `settings` · `inbox`
 */
import { SlideOverPane, Search, Tab, Tabs, Toggle, IconButton, icons } from '@elizmonday/ui';
import { useCallback, useMemo, useState } from 'react';
import { TaskSettingsPanel } from '../tasks/TaskSettingsPanel';
import { createFixtureTasksSource } from '../tasks/fixture';
import { useTaskSettings } from '../tasks/settings-store';
import { InboxScreen, type InboxTabId } from './InboxScreen';
import { NotificationSettings } from './NotificationSettings';
import { NotificationsPanel } from './NotificationsPanel';
import { createFixtureSource } from './fixture';
import type { NotificationsSource } from './types';
import './notifications.css';
/* The pane is the Tasks module's own component, so it needs the Tasks stylesheet here. */
import '../tasks/tasks.css';

/**
 * **P-321** · the settings screen, with **both** surfaces of the Tasks switches on one page.
 *
 * The page's own Tasks section is at the end of the notification settings; the `Open Tasks
 * preferences` button mounts the real `TaskSettingsPanel` over it, against the **same** fixture
 * `TasksSource`. That is what makes the card's outcome provable without a server: flip the digest
 * here, open the pane, and the pane shows what was flipped — one `tasks.settings` store, both
 * surfaces repainting, rather than two components that happen to agree on first load.
 *
 * The button exists only in the harness; the product has two routes and a link between them.
 */
function SettingsHarnessScreen({ source }: { source: NotificationsSource }) {
  const tasksSource = useMemo(() => createFixtureTasksSource(), []);
  const { settings, save } = useTaskSettings(tasksSource);
  const [paneOpen, setPaneOpen] = useState(false);

  return (
    <div data-testid="ui-notifications-settings">
      <NotificationSettings source={source} tasks={tasksSource} />
      <div style={{ padding: '0 32px 64px' }}>
        <button
          type="button"
          data-testid="harness-open-tasks-preferences"
          onClick={() => setPaneOpen(true)}
        >
          Open Tasks preferences
        </button>
      </div>
      {paneOpen && settings ? (
        <TaskSettingsPanel settings={settings} onChange={save} onClose={() => setPaneOpen(false)} />
      ) : null}
    </div>
  );
}

export function NotificationsHarness() {
  const params = new URLSearchParams(typeof window === 'undefined' ? '' : window.location.search);
  const screen = params.get('screen') ?? 'bell';
  const source = useMemo(() => createFixtureSource(), []);
  const [tab, setTab] = useState(0);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [query, setQuery] = useState('');
  const [inboxTab, setInboxTab] = useState<InboxTabId>('all_updates');
  const [lastNavigation, setLastNavigation] = useState<string | null>(null);
  const navigateFromFeed = useCallback((href: string) => {
    window.history.pushState(window.history.state, '', href);
    setLastNavigation(href);
  }, []);

  if (screen === 'settings') {
    return <SettingsHarnessScreen source={source} />;
  }

  if (screen === 'inbox') {
    return (
      <div
        className="ez-dialog-backdrop"
        style={{ paddingBlockStart: 32 }}
        data-testid="ui-notifications-inbox"
      >
        <InboxScreen source={source} tab={inboxTab} onTabChange={setInboxTab} onNavigate={navigateFromFeed} />
        {/*
          P-325: the harness has no session, so the board / item routes cannot render here. The
          breadcrumb's navigation is still made observable as an **outcome**: the URL changes to
          the link's target (`history.pushState`, which the data router does not act on) and
          the last target is echoed for the verifier.
        */}
        <output data-testid="harness-last-navigation" hidden>
          {lastNavigation ?? ''}
        </output>
      </div>
    );
  }

  return (
    <div data-testid="ui-notifications-bell" style={{ minHeight: '100vh' }}>
      <SlideOverPane
        open
        onClose={() => undefined}
        title="Notifications"
        data-testid="notifications-pane"
        headerActions={
          <>
            <IconButton icon={icons.settings} ariaLabel="Change notification settings" size="xs" kind="tertiary" />
            <IconButton icon={icons.moreActions} ariaLabel="Notifications Options" size="xs" kind="tertiary" />
          </>
        }
        toolbar={
          <>
            <Tabs activeTabId={tab} onTabChange={setTab} data-testid="notifications-tabs">
              <Tab value={0}>All</Tab>
              <Tab value={1}>Mentioned</Tab>
              <Tab value={2}>Assigned to me</Tab>
            </Tabs>
            <div className="ez-pane-filter-row">
              <Search
                placeholder="Search notifications by people, boards, and more..."
                inputAriaLabel="Search notifications"
                value={query}
                onChange={setQuery}
                data-testid="notifications-search"
              />
              <Toggle
                isSelected={unreadOnly}
                onChange={setUnreadOnly}
                aria-label="Unread only"
                data-testid="notifications-unread-only"
              />
              <span className="ez-pane-filter-row__label">Unread only</span>
            </div>
          </>
        }
      >
        <NotificationsPanel
          source={source}
          tab={(['all', 'mentioned', 'assigned'] as const)[tab] ?? 'all'}
          unreadOnly={unreadOnly}
          query={query}
        />
      </SlideOverPane>
    </div>
  );
}
